import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	BindingEngine,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';

import { AutocompleteColumnHeader } from '~/components/field/common/autocomplete/autocomplete-react';
import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import { FieldModel } from '~/models/form-model';

@autoinject
@customElement('a-country')
@useView(PLATFORM.moduleName('./country.html'))
export class Country extends Field {
	/**
	 * Used by view
	 */
	private readonly itemColumnName = 'code';

	/**
	 * Used by view
	 */
	private readonly itemColumnDescription = 'description';

	private readonly columnHeaders: AutocompleteColumnHeader[] = [
		{
			columnName: this.itemColumnDescription,
			display: 'Country',
		},
		{
			columnName: this.itemColumnName,
			display: 'Code',
		},
	];

	private items: Array<{ code: string; description: string }> = [];

	@bindable private stateField: Field;

	@bindable private postcodeField: Field;

	constructor(
		readonly element: Element,
		protected parentForm: Form,
		protected bindingEngine: BindingEngine,
		readonly eventAggregator: EventAggregator,
	) {
		super(Field.TYPE.COUNTRY, element, parentForm, eventAggregator);
	}

	public isBlankOrAustralia(): boolean {
		return !this.value || this.value === 'AU' || this.value === 'AUSTRALIA';
	}

	protected valueChanged(value, oldValue) {
		super.valueChanged(value, oldValue);
		if (
			this.value &&
			this.value !== 'AU' &&
			this.value !== 'AUSTRALIA' &&
			this.postcodeField
		) {
			// NOTE: Jake: 2018-11-01 - WEBUI-1122
			// Set the postcode to "9999" if the country is not "Australia"
			this.postcodeField.value = '9999';
		}
	}

	protected fieldModelChanged(fieldModel: FieldModel) {
		super.fieldModelChanged(fieldModel);

		// Load list of countries from fieldModel
		if (this.fieldModel) {
			const countryList = this.fieldModel.codeList;
			if (countryList && countryList.length > 0) {
				this.items = countryList;
			}
		}

		// Trim country name in description which is from the code list
		this.description = this.trimDescription(this.description);
	}

	/**
	 * Used by view
	 */
	private readonly onSelectAutocomplete = (
		record: { [columnName: string]: any } | undefined,
	): void => {
		this.description = '';
		if (!this.items || !record || !record[this.itemColumnName]) {
			return;
		}
		const countryCode = record[this.itemColumnName];
		const country = this.items.find((item) => item.code === countryCode);
		if (country && country.description) {
			// only trim selected country description and keep the description in search result list unchanged
			this.description = this.trimDescription(country.description);
		}
	};

	/**
	 * Country description is a pattern like AUS-Australia in C&D table and fetched from search API. The
	 * leading country code and hyphen should be trimmed.
	 */
	private trimDescription(description: string): string {
		if (!description) {
			return '';
		}
		const index = description.indexOf('-');
		return index > -1 ? description.substring(index + 1) : description;
	}
}
